import { useNavigate, useParams } from 'react-router-dom';
import { Box, Flex, Icon, SimpleGrid, Text } from '@chakra-ui/react';
import {
  ViewfinderCircleIcon,
  SparklesIcon,
  ArrowTrendingUpIcon,
  Square3Stack3DIcon,
  DocumentIcon,
  BookOpenIcon,
  EllipsisHorizontalIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import {
  DialogBackdrop,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from '@/components/ui/dialog';
import useContentTypesStore from '@/utils/use-content-type';
import useCanManageContentTypes from '@/lib/hooks/use-can-manage-content-types';

export type PickerMode = 'create' | 'browse';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  mode: PickerMode;
};

type IconChoice = {
  icon: typeof ViewfinderCircleIcon;
  bg: string;
  description: string;
};

function pickIcon(name: string): IconChoice {
  const n = name.toLowerCase();
  if (n.includes('drill')) {
    return { icon: ViewfinderCircleIcon, bg: 'red.100', description: 'Practice exercise' };
  }
  if (n.includes('skill')) {
    return { icon: SparklesIcon, bg: 'green.100', description: 'Technique breakdown' };
  }
  if (n.includes('play')) {
    return { icon: ArrowTrendingUpIcon, bg: 'blue.100', description: 'Diagram with routes' };
  }
  if (n.includes('tactic')) {
    return { icon: Square3Stack3DIcon, bg: 'purple.100', description: 'Game situation' };
  }
  if (n.includes('document')) {
    return { icon: DocumentIcon, bg: 'orange.100', description: 'PDF or handout' };
  }
  if (n.includes('article') || n.includes('post')) {
    return { icon: BookOpenIcon, bg: 'cyan.100', description: 'Long-form read' };
  }
  return { icon: DocumentIcon, bg: 'gray.100', description: 'Workspace content' };
}

function pluralize(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return trimmed;
  if (/s$/i.test(trimmed)) return trimmed;
  if (/y$/i.test(trimmed)) return trimmed.replace(/y$/i, 'ies');
  return `${trimmed}s`;
}

const COPY: Record<PickerMode, { title: string; subtitle: string }> = {
  create: {
    title: 'What do you want to create?',
    subtitle: 'Start a blank item in a library — a drill, play, or article.',
  },
  browse: {
    title: 'What do you want to browse?',
    subtitle: 'Jump into one of your content libraries.',
  },
};

export default function ContentTypePickerDialog({ isOpen, onClose, mode }: Props) {
  const navigate = useNavigate();
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const { contentTypes } = useContentTypesStore();
  const copy = COPY[mode];
  const canManageContentTypes = useCanManageContentTypes();

  const targetForType = (contentTypeId: number) =>
    mode === 'create'
      ? `/${workspaceId}/content-types/${contentTypeId}/contents/create`
      : `/${workspaceId}/content-types/${contentTypeId}/contents/workspace-library`;

  const handleSelect = (contentTypeId: number) => {
    onClose();
    navigate(targetForType(contentTypeId));
  };

  const handleOther = () => {
    onClose();
    navigate(`/${workspaceId}/content-types`);
  };

  const tileLabel = (name: string) =>
    mode === 'create' ? `New ${name.toLowerCase()}` : pluralize(name);

  return (
    <DialogRoot lazyMount open={isOpen} onOpenChange={(e) => !e.open && onClose()}>
      <DialogBackdrop />
      <DialogContent maxW='600px'>
        <DialogCloseTrigger />
        <DialogHeader>
          <DialogTitle>{copy.title}</DialogTitle>
          <Text fontSize='sm' color='gray.500' mt={1}>
            {copy.subtitle}
          </Text>
        </DialogHeader>
        <DialogBody pb={6}>
          <SimpleGrid columns={{ base: 1, md: 2 }} gap={3} mb={3}>
            {contentTypes.map((ct) => {
              const choice = pickIcon(ct.name);
              return (
                <Flex
                  key={ct.id}
                  as='button'
                  type='button'
                  onClick={() => handleSelect(ct.id)}
                  align='center'
                  gap={3}
                  p={3}
                  borderWidth={1}
                  borderColor='gray.200'
                  borderRadius='lg'
                  bg='white'
                  textAlign='left'
                  width='100%'
                  transition='all 0.15s'
                  _hover={{ borderColor: 'gray.300', bg: 'gray.50' }}
                >
                  <Flex
                    align='center'
                    justify='center'
                    w={10}
                    h={10}
                    borderRadius='md'
                    bg={choice.bg}
                    flexShrink={0}
                  >
                    <Icon as={choice.icon} w={5} h={5} color='gray.800' />
                  </Flex>
                  <Box flex='1' minW={0}>
                    <Text fontSize='sm' fontWeight='semibold' color='gray.900'>
                      {tileLabel(ct.name)}
                    </Text>
                    <Text fontSize='xs' color='gray.500'>
                      {choice.description}
                    </Text>
                  </Box>
                  <Icon as={ChevronRightIcon} w={4} h={4} color='gray.400' flexShrink={0} />
                </Flex>
              );
            })}
          </SimpleGrid>
          {canManageContentTypes && (
            <Flex
              as='button'
              type='button'
              onClick={handleOther}
              align='center'
              justify='center'
              gap={2}
              p={3}
              borderWidth={1}
              borderColor='gray.300'
              borderRadius='lg'
              borderStyle='dashed'
              bg='transparent'
              width='100%'
              transition='all 0.15s'
              _hover={{ bg: 'gray.50' }}
            >
              <Icon as={EllipsisHorizontalIcon} w={5} h={5} color='gray.600' />
              <Text fontSize='sm' fontWeight='medium' color='gray.700'>
                Other
              </Text>
            </Flex>
          )}
        </DialogBody>
      </DialogContent>
    </DialogRoot>
  );
}
